import { type JVSIcons, Label, type LabelProps } from '@mairistem/composants';
import { Sens } from '@mairistem/comptabilite';
import { Icon } from '@mairistem/finances-utils';
import Decimal from 'decimal.js';
import React from 'react';
import AUTORISATION_MODAL_ERROR from '../constants/autorisation/autorisationModalError';
import STATUT_PROJET from '../constants/common/statutProjet';
import type Financement from '../interfaces/common/financement';
import type Projet from '../interfaces/common/projet';
import './autorisation.css';

export const renderLabel = (statut: number) => {
	let styles = {
		color: 'black',
		content: 'Inconnu',
		icon: 'CircleAlert',
	} as LabelProps;

	switch (statut) {
		case STATUT_PROJET.EN_COURS:
			styles = { color: 'blue', content: 'En cours', icon: 'Loader' };
			break;
		case STATUT_PROJET.BROUILLON:
			styles = { color: 'grey', content: 'Brouillon', icon: 'FilePen' };
			break;
		case STATUT_PROJET.SOLDE:
			styles = { color: 'green', content: 'Soldée', icon: 'Check' };
			break;
		default:
			break;
	}

	return (
		<Label
			basic
			circular
			className="autorisationStatusLabel"
			color={styles.color}
		>
			<Icon
				color={styles.color}
				iconSet="Lucide"
				name={styles.icon as JVSIcons}
				size={16}
			/>
			{styles.content}
		</Label>
	);
};

export const validateStepInformations = (
	autorisation: Projet,
	isServiceComboboxVisible: boolean,
) => {
	const result = { error: null, validated: false };

	if (autorisation.code.length === 0) {
		result.error = AUTORISATION_MODAL_ERROR.CODE_REQUIRED;
		return result;
	}

	if (isServiceComboboxVisible && !autorisation.identifiantService) {
		result.error = AUTORISATION_MODAL_ERROR.SERVICE_REQUIRED;
		return result;
	}

	if (autorisation.libelle.length === 0) {
		result.error = AUTORISATION_MODAL_ERROR.LIBELLE_REQUIRED;
		return result;
	}

	if (!autorisation.identifiantEtiquette) {
		result.error = AUTORISATION_MODAL_ERROR.ETIQUETTE_REQUIRED;
		return result;
	}

	if (autorisation.montantAutorisation === 0) {
		result.error = AUTORISATION_MODAL_ERROR.MONTANT_REQUIRED;
		return result;
	}

	if (autorisation.composants.length > 0) {
		let montantTotalComposants = new Decimal(0);

		autorisation.composants.some((composant) => {
			if (composant.libelle.length === 0) {
				result.error = AUTORISATION_MODAL_ERROR.COMPOSANT_LIBELLE_REQUIRED;
				return true;
			}

			if (composant.montantEvenement === 0) {
				result.error = AUTORISATION_MODAL_ERROR.COMPOSANT_MONTANT_REQUIRED;
				return true;
			}

			montantTotalComposants = montantTotalComposants.add(
				composant.montantEvenement,
			);

			return false;
		});

		if (result.error) return result;

		if (montantTotalComposants.gt(autorisation.montantAutorisation)) {
			result.error = AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_EVENEMENT_INCORRECT;
			return result;
		}
	}

	result.validated = true;
	return result;
};

export const validateStepCreditsPaiementRessources = (
	autorisation: Projet,
	hasEvenements: boolean,
	sens: string,
) => {
	const result = { error: null, validated: false };

	const validateFinancements = (
		financements: Financement[],
		montantAutorisationOrComposant: number,
		libelle?: string,
	) => {
		let totalMontantsFinancement = new Decimal(0);

		// Check financement
		const hasErrorInFinancements = (financements ?? []).some((financement) => {
			if (financement.sens !== sens) return false;

			if (!financement.exercice) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_EXERCICE_REQUIRED(
					sens,
					libelle,
				);
				return true;
			}

			if (String(financement.exercice).length !== 4) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_EXERCICE_INCORRECT(
					sens,
					libelle,
				);
				return true;
			}

			if (financement.montant === 0) {
				result.error = AUTORISATION_MODAL_ERROR.FINANCEMENT_MONTANT_REQUIRED(
					sens,
					financement.exercice,
					libelle,
				);
				return true;
			}

			// Check proImputation
			let totalMontantsProImputation = new Decimal(0);

			const hasErrorInProImputations = financement.proImputations.some(
				(proImputation) => {
					if (!proImputation.codeArticle && !proImputation.codeChapitre) {
						result.error =
							AUTORISATION_MODAL_ERROR.PRO_IMPUTATION_IMPUTATION_REQUIRED(
								sens,
								financement.exercice,
								libelle,
							);
						return true;
					}

					if (financement.sens === Sens.DEPENSE) {
						totalMontantsProImputation = totalMontantsProImputation
							.add(proImputation.montant)
							.add(proImputation.revision)
							.add(proImputation.rarN1)
							.sub(proImputation.rarN);
					}

					return false;
				},
			);

			// S'il y a des erreurs dans les proImputations, on sort de la boucle des financements
			if (hasErrorInProImputations) return true;

			const financementMontant = new Decimal(financement.montant)
				.add(financement.revision)
				.add(financement.rarN1)
				.sub(financement.rarN);

			if (totalMontantsProImputation.gt(financementMontant)) {
				result.error =
					AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_PRO_IMPUTATION_INCORRECT(
						sens,
						financement.exercice,
						libelle,
					);
				return true;
			}

			totalMontantsFinancement =
				totalMontantsFinancement.add(financementMontant);

			return false;
		});

		// S'il y a des erreurs dans les financements, on sort de la boucle des composants
		if (hasErrorInFinancements) return true;

		if (totalMontantsFinancement.gt(montantAutorisationOrComposant)) {
			result.error =
				AUTORISATION_MODAL_ERROR.TOTAL_MONTANT_FINANCEMENT_INCORRECT(
					sens,
					libelle,
				);
			return true;
		}

		return false;
	};

	if (hasEvenements) {
		const hasErrorInComposants = autorisation.composants.some((composant) =>
			validateFinancements(
				composant.financements,
				composant.montantEvenement,
				composant.libelle,
			),
		);

		if (hasErrorInComposants) return result;
	} else {
		const hasErrorInFinancements = validateFinancements(
			autorisation.financements,
			autorisation.montantAutorisation,
		);

		if (hasErrorInFinancements) return result;
	}

	result.validated = true;
	return result;
};
